import Api from "@root/components/api";
import { group } from "d3-array";

export default class MappMutation {
    async loadGroups() {
        let query = `query {
            currentUser {
                students {
                    owner manager
                    user { id visualName}
                    team {
                        id name
                        primarySubject {
                            id name
                        }
                    }
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query });
        if (result.errors) {
            return;
        }

        return result.data.currentUser?.students;
    } 
    
    async loadGroup(groupId) {
        let query = `query($groupId: String!) {
            team(id: $groupId) {
                id name 
                students { 
                    owner manager
                    user { id visualName }                    
                }
                primarySubject {
                    id, name
                }
                cardLists {
                    id, name
                    primarySubject {
                        id, name
                    }
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
        } });
        if (result.errors) {
            return;
        }

        return result.data.team;
    } 
    
    async createGroup(name, subjectId) {
        let query = `mutation ($name: String!, $subjectId: String!) {
            createGroup(name: $name, primarySubjectId: $subjectId)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            name: name,
            subjectId: subjectId,
        }});
        return result.data?.createGroup;
    }    
    
    async joinGroup(groupId) {
        let query = `mutation ($groupId: String!) {
            joinGroup(groupId: $groupId)
        }`;
        
        return await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
        }});
    }
    
    async removeGroup(groupId) {
        let query = `mutation ($groupId: String!) {
            removeGroup(groupId: $groupId)
        }`;
        
        return await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
        }});
    }
    
    async leaveGroup(groupId) {
        let query = `mutation ($groupId: String!) {
            leaveGroup(groupId: $groupId)
        }`;
        
        return await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
        }});
    }
        
    async makeManager(groupId, userId, manager) {
        let query = `mutation ($groupId: String!, $userId: String!, $manager: Boolean!) {
            makeManager(groupId: $groupId, userId: $userId, manager: $manager)
        }`;
        
        return await Api.postData('/g', { query: query, variables: {
            groupId: groupId, 
            userId: userId,
            manager: manager,
        }});
    }
    
    async scheduleMeForToday() {
        let query = `mutation{
            scheduleMeForToday
        }`;
        
        return await Api.postData('/g', { query: query });
    }
    
    async scheduleGroupForToday(groupId) {
        let query = `mutation ($groupId: String!) {
            scheduleGroupForToday(groupId: $groupId)
        }`;
        
        return await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
        } });
    }
    
    async loadCardLists(subjectId) {
        let query = `query($subjectId: String!) {
            cardListsForSubject(subjectId: $subjectId) {
                id, name, description, study, cardCount, gameCount, free, bought,
                authorId, authorName, canLike, likes, authorAvatar
                primarySubject { id, name }
                topCards {
                    id, name, 
                    skill {
                        id, name, iknow, study, remindAt
                    }
                }
                topGames {
                    id, name, likes, canLike, gameType, autoCheck,
                    imageUrl, description, canEdit, my, approved, published,
                    count, time, gameUrl, subjects, language, requirements,
                    primarySkill { 
                        id, name, iknow, exercisePoints,
                        primarySubject { id, name } 
                    },
                    authorId, authorName,
                    skills { id, name, iknow }
                }
            }    
                }`;
        let result = await Api.postData('/g', { query: query, variables: {
            subjectId: subjectId,
        } });
        if (result.errors) {
            return;
        }

        return result.data.cardListsForSubject;
    } 
    
    async addCardListToGroup(groupId, cardListId) {
        let query = `mutation ($groupId: String!, $cardListId: String!) {
            addCardListToGroup(groupId: $groupId, cardListId: $cardListId)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
            cardListId: cardListId,
        }});
        return result.data.addCardListToGroup;
    }
    
    async removeCardListFromGroup(groupId, cardListId) {
        let query = `mutation ($groupId: String!, $cardListId: String!) {
            removeCardListFromGroup(groupId: $groupId, cardListId: $cardListId)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            groupId: groupId,
            cardListId: cardListId,
        }});
        return result.data.removeCardListFromGroup;
    }

    async loadHomeWorkHistory(userId) {
        let query = `mutation ($userId: String!) {
            loadHomeWorkHistory(userId: $userId) {
                skills {
                    id name
                }
                groups {
                    id name
                    cardLists {id name}
                }
                decks {
                    id name
                }
                total {
                    start end
                    doneCount undoneCount timeSpent skillsCount speedLevel knowledgeLevel
                }
                byDeck {
                    start end
                    doneCount undoneCount timeSpent skillsCount speedLevel knowledgeLevel
                }
            }
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            userId: userId,
        }});
        return result.data.loadHomeWorkHistory;
    }
   
    async loadTodos(listId) {
        let query = `query($id: String!) {
            cardList(id: $id) {
                id, name
                todos {
                    id, seed, expectedTime, done requiredSuccessfulAttempts successes
                    skill {
                        id, name, iknow, study, remindAt
                    }
                    card {
                        id, name, 
                        skill {
                            id, name, iknow, study, easyFactor, repetition, interval, remindAt
                        }
                    }
                    game {
                        id, name, answerType, imageUrl, gameUrl,
                        autoCheck, count, gameType, time, language, requirements,
                        authorId, authorName, authorAvatar, public, likes, canLike,
                        primarySkill {
                            id, name, iknow, exercisePoints,
                            primarySubject { id, name }
                        },
                        fieldSets { id },
                        authorId, authorName,
                        skills { id, name, iknow }
                    }
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {id: listId} });
        if (result.errors) {
            return;
        }
        return result.data.cardList;
    }
  
    async loadGraspsToDo(listId, count) {
        let query = `query($id: String!, $count: Int!) {
            cardList(id: $id) {
                id, name
                graspsToDo(count: $count) {
                    id seed quality speedLevel knowledgeLevel stage remindAt 
                    workout { id }
                    skill {
                        id, name, iknow, study, remindAt
                    }
                    card {
                        id, name, 
                        skill {
                            id, name, iknow, study, easyFactor, repetition, interval, remindAt
                        }
                    }
                    game {
                        id, name, answerType, imageUrl, gameUrl,
                        autoCheck, count, gameType, time, language, requirements,
                        authorId, authorName, authorAvatar, public, likes, canLike,
                        primarySkill {
                            id, name, iknow, exercisePoints,
                            primarySubject { id, name }
                        },
                        fieldSets { id },
                        authorId, authorName,
                        skills { id, name, iknow }
                    }
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {id: listId, count: count} });
        if (result.errors) {
            return;
        }
        return result.data.cardList;
    }
    
  
    async userGrasps(listId, userId) {
        let query = `query($listId: String!, $userId: String!) {
            cardList(id: $listId) {
                id, name
                graspsOf(userId: $userId) {
                    id quality speedLevel knowledgeLevel stage
                    workout { id name skill {id name} game {name} }
                }
            }
            user(id: $userId) {
                id visualName coins
            }
    }`;
        let result = await Api.postData('/g', { query: query, variables: {listId: listId, userId: userId} });
        if (result.errors) {
            return;
        }
        return result.data;
    }
     
    async completeTodo(todoId, correct, time) {
        let query = `mutation ($todoId: String!, $correct: Boolean!, $time: Float!) {
            completeTodo(todoId: $todoId, correct: $correct, time: $time) {
                id, done, correct, levelBefore, levelAfter, remindAt speedBefore speedAfter currentSpeed remindIn expectedTime time averageTime successes
                freeMaterial {
                    lessonUrl videoUrl previewUrl url materialType course
                }
                paidMaterial {
                    lessonUrl videoUrl previewUrl url materialType course hasAccess
                }
            }
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            todoId: todoId,
            correct: correct,
            time: time,
        }});
        return result.data.completeTodo;
    }
          
    async completeWorkout(workoutId, correct, time) {
        let query = `mutation ($workoutId: String!, $correct: Boolean!, $time: Float!) {
            completeWorkout(workoutId: $workoutId, correct: $correct, time: $time) {
                levelBefore levelAfter time expectedTime currentSpeed speedBefore speedAfter averageTime
                freeMaterial {
                    lessonUrl videoUrl previewUrl url materialType course
                }
                paidMaterial {
                    lessonUrl videoUrl previewUrl url materialType course hasAccess
                }                
            }
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            workoutId: workoutId,
            correct: correct,
            time: time,
        }});
        return result.data.completeWorkout;
    }
          
    async learn(skillId, correct, time) {
        let query = `mutation ($skillId: String!, $correct: Boolean!, $time: Float!) {
            learn(skillId: $skillId, correct: $correct, time: $time) {
                levelBefore levelAfter time expectedTime currentSpeed speedBefore speedAfter averageTime
                freeMaterial {
                    lessonUrl videoUrl previewUrl url materialType course
                }
                paidMaterial {
                    lessonUrl videoUrl previewUrl url materialType course hasAccess
                }                
            }
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            skillId: skillId,
            correct: correct,
            time: time,
        }});
        return result.data.learn;
    }
     
    async studyCard(workoutId, study) {
        let query = `mutation ($workoutId: String!, $study: Boolean!) {
            studyCard(workoutId: $workoutId, study: $study)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            workoutId: workoutId,
            study: study,
        }});
        return result.data.studyCard;
    }
     
    async studyStudentCard(workoutId, studentId, study) {
        let query = `mutation ($workoutId: String!, $studentId: String!, $study: Boolean!) {
            studyStudentCard(workoutId: $workoutId, studentId: $studentId, study: $study)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            workoutId: workoutId,
            studentId: studentId,
            study: study,
        }});
        return result.data.studyStudentCard;
    }

    async checkCodeTask(gameId, init, code, test) {
        let result = await Api.postData('/api/checkCodeTask', { gameId: gameId, code: code, init: init, test: test });
        return result;
    }

    async loadSupervisor(userId) {
        let query = `query($userId: String!) {
            user(id: $userId) {
                visualName
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {userId: userId} });
        if (result.errors) {
            return;
        }

        return result.data.user;
    }    
    
    async addSupervisor(supervisorId) {
        let query = `mutation ($supervisorId: String!) {
            addSupervisor(supervisorId: $supervisorId)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            supervisorId: supervisorId,
        }});
        return result.data.addSupervisor;
    }
    
    async scheduleTomorrows(cardListId, count) {
        let query = `mutation ($cardListId: String!, $count: Int!) {
            scheduleTomorrows(cardListId: $cardListId, count: $count)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            count: count,
            cardListId: cardListId,
        }});
        return result.data.scheduleTomorrows;
    }    

    async loadFamily(userId) {
        let query = `query {
            currentUser {
                id visualName
                children {
                    id visualName
                    studyGroupProgress {
                        team {id name}
                        lists {
                            list {id name}
                            count avgKnowledgeLevel avgSpeedLevel
                        }
                    }
                }
                supervisors {
                    id visualName
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {userId: userId} });
        if (result.errors) {
            return;
        }

        return result.data.currentUser;
    }    

    async loadChildDetails(childId) {
        let query = `query($userId: String!) {
            user(id: $userId) {
                id visualName
                userCardLists {
                    id name
                    todosOf(userId: $userId) {
                        id seed expectedTime done requiredSuccessfulAttempts timeSpent attempts successes
                        game {
                            id
                        }
                        grasp {
                            knowledgeLevel speedLevel averageTime remindAt
                        }
                        skill {
                            id name iknow study remindAt
                        }
                   }
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {userId: childId} });
        if (result.errors) {
            return;
        }

        return result.data.user;
    }    

    async loadSetGoal(userId, teamId) {
        let query = `query($userId: String!, $teamId: String!) {
            user(id: $userId) {
                id visualName
                quality(teamId: $teamId)
                learnedSkillCount(teamId: $teamId)
                studySkillCount(teamId: $teamId)
            }
            team(id: $teamId) {
                id name
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {userId: userId, teamId: teamId} });
        if (result.errors) {
            return;
        }

        return result.data;
    }    

    async loadUserGoals(userId) {
        let query = `query($userId: String!) {
            user(id: $userId) {
                id visualName coins
                students {
                    coinLevel coinCollectedDate coinLevelUpDate
                    team {id name}
                    dailyHomeTasks {
                        id type progress tasks completed
                    }
                    goals {
                        id goalType isAbsolute skillCount quality reward endTime authorName authorId
                        finished done awarded
                    }
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {userId: userId} });
        if (result.errors) {
            return;
        }

        return result.data.user;
    }    

    async sendReport() {
        let query = `mutation {
            sendReport()
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
        }});
        return result.data.sendReport;
    }

    async collectAward(type, goalId) {
        let query = `mutation($goalId: String!, $type: StudyGoalAwardTypes!) {
            collectAward(goalId: $goalId, type: $type)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {
            goalId: goalId,
            type: type,
        }});
        return result.data.collectAward;
    }

    async loadSubscription() {
        let query = `query() {
            currentUser {
                tgSubscriptions {
                    id childrenDailyReports selfDailyReports selfReminder
                }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {} });
        return result.data?.currentUser?.tgSubscriptions;
    }

    async saveTgSubscription(id, selfReminder, selfDailyReports, childrenDailyReports) {
        let query = `mutation ($id: String!, $selfReminder: String, $selfDailyReports: String, $childrenDailyReports: String) {
            saveTgSubscription(id: $id, selfReminder: $selfReminder,  selfDailyReports: $selfDailyReports, childrenDailyReports: $childrenDailyReports)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            id: id,
            selfReminder: selfReminder,
            selfDailyReports: selfDailyReports,
            childrenDailyReports: childrenDailyReports,            
        }});
        return result.data.saveTgSubscription;
    }
    
    async removeTgSubscription(id) {
        let query = `mutation ($id: String!) {
            removeTgSubscription(id: $id)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            id: id
        }});
        return result.data.removeTgSubscription;
    }

    async setUserGoal(userId, teamId, absolute, skillCount, quality, expire, reward) {
        let query = `mutation ($userId: String!, $teamId: String!, $absolute: Boolean!, $skillCount: Int!, $quality: Int!, $expire: DateTime, $reward: String!) {
            setUserGoal(userId: $userId, teamId: $teamId, absolute: $absolute, skillCount: $skillCount, quality: $quality, expire: $expire, reward: $reward)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            userId: userId, 
            teamId: teamId, 
            absolute: absolute, 
            skillCount: skillCount, 
            quality: quality, 
            expire: expire, 
            reward: reward,
        }});
        return result.data.setUserGoal;
    }
    
    async deleteUserGoal(goalId) {
        let query = `mutation ($goalId: String!) {
            deleteUserGoal(goalId: $goalId)
        }`;
        
        const result = await Api.postData('/g', { query: query, variables: {            
            goalId: goalId,
        }});
        return result.data.deleteUserGoal;
    }
    
}